import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import { startRegistration } from '@simplewebauthn/browser';
import { useState } from 'react';
import { useRouteLoaderData } from 'react-router-dom';
import { toast } from 'react-toastify';
import ToastMessage from '@/components/toasts/ToastMessage';
import auth from '@/services/AuthService';

export default function WebAuthnForm() {
  const { me } = useRouteLoaderData('auth');
  const [loading, setLoading] = useState(false);
  const [authenticators, setAuthenticators] = useState(
    me?.authenticators?.filter(
      (authenticator) => authenticator.type === 'public-keys',
    ) || [],
  );

  const deleteWebauthn = async () => {
    try {
      await auth.post('/webauthn/deleteWebAuthn');
      setAuthenticators([]);
      toast.success(
        <ToastMessage
          title="Erfolg"
          subtitle="WebAuthn erfolgreich gelöscht"
        />,
      );
    } catch (e) {
      toast.error(
        <ToastMessage title="Fehler" subtitle={e?.response?.data?.message} />,
      );
    }
  };

  const fetchRegistrationData = async () => {
    try {
      const res = await auth.get('/webauthn/registration');
      const { data } = res;
      return data;
    } catch (e) {
      toast.error(<ToastMessage title="Fehler" subtitle={e.message} />);
    }
    return null;
  };

  const handleRegister = async () => {
    setLoading(true);
    const registrationOptions = await fetchRegistrationData();
    if (!registrationOptions) return;
    try {
      const attResp = await startRegistration({
        optionsJSON: registrationOptions,
      });
      const verificationsResp = await auth.post(
        '/webauthn/registration',
        attResp,
      );
      if (verificationsResp.status !== 204) {
        toast.error(
          <ToastMessage
            title="Fehler"
            subtitle={'Failed to verify registration!'}
          />,
        );
      } else {
        toast.success(
          <ToastMessage
            title="Erfolg"
            subtitle="Gerät erfolgreich registriert"
          />,
        );
        const res = await auth.get('/webauthn/getWebAuthnAuthenticators');
        setAuthenticators(res.data);
      }
    } catch (e) {
      // Delete temporary registration options.
      await auth.post('/webauthn/deleteRegistrationOptions');
      toast.error(<ToastMessage title="Fehler" subtitle={e.message} />);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        mt: 4,
        display: 'flex',
        flexFlow: 'column',
        px: 2,
      }}
    >
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            my: 4,
          }}
        >
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>
            Bitte haben Sie etwas Geduld, während wir ihr Gerät registrieren...
          </Typography>
        </Box>
      ) : (
        <Box sx={{ width: '100%', maxWidth: 500 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              marginBottom: '20px',
            }}
          >
            Registriere ein Gerät mittels WebAuthn
          </Typography>
          <Alert sx={{ mb: 2 }} severity="info">
            WebAuthn ermöglicht es Ihnen, Ihr aktuelles Gerät als zweiten Faktor
            für die Authentifizierung zu verwenden. Verwenden Sie diese Funktion
            bitte nicht auf öffentlichen Geräten! Falls Ihr lokaler Schlüssel
            gelöscht wurde, müssen Sie sich erneut registrieren. Um zu
            überprüfen, ob Ihr lokaler Schlüssel gelöscht wurde, können Sie
            unter Windows in den Einstellungen unter „Konten“ -
            „Sicherheitsschlüssel“ nachsehen, ob ein Schlüssel für codemeta.de
            hinterlegt ist.
          </Alert>
          {authenticators.length > 0 ? (
            <Button
              fullWidth
              variant="contained"
              color="secondary"
              onClick={deleteWebauthn}
            >
              Delete
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleRegister}
            >
              Gerät registrieren
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}
